import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { MessageCircle, Send, Loader2, ArrowLeft, User, Search, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminChats = () => {
  const { token, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [messages, setMessages] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const messagesEndRef = useRef(null);
  const prevMsgCount = useRef(0);
  const prevConvUnread = useRef(0);
  const audioRef = useRef(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
    audioRef.current.volume = 0.5;
  }, []);

  const playSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {});
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!isSuperAdmin()) { navigate('/dashboard'); return; }
    fetchConversations();
    const convInterval = setInterval(fetchConversations, 5000);
    return () => clearInterval(convInterval);
  }, []);

  useEffect(() => {
    if (!selectedCustomer) return;
    fetchMessages(selectedCustomer);
    const interval = setInterval(() => fetchMessages(selectedCustomer), 5000);
    return () => clearInterval(interval);
  }, [selectedCustomer]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/admin/chats`, { headers: { Authorization: `Bearer ${token}` } });
      const newConversations = res.data;

      // Play sound when total unread across conversations increases
      const totalUnread = newConversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      if (!isFirstLoad.current && totalUnread > prevConvUnread.current) {
        playSound();
        toast.info('New message from a customer');
      }
      isFirstLoad.current = false;
      prevConvUnread.current = totalUnread;

      setConversations(newConversations);
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = useCallback(async (customerId) => {
    try {
      const res = await axios.get(`${API}/admin/chats/${customerId}`, { headers: { Authorization: `Bearer ${token}` } });
      const newMessages = res.data.messages;

      // Play sound for new customer messages in active chat
      if (newMessages.length > prevMsgCount.current) {
        const latestMsg = newMessages[newMessages.length - 1];
        if (latestMsg?.sender_type === 'customer') {
          playSound();
        }
      }
      prevMsgCount.current = newMessages.length;

      setMessages(newMessages);
      setCustomerInfo(res.data.customer);
    } catch (error) {
      console.error('Failed to load messages');
    }
  }, [token, playSound]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedCustomer) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/chats/${selectedCustomer}`, { message: newMessage.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setNewMessage('');
      fetchMessages(selectedCustomer);
      fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(c =>
    c.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.customer_phone?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-130px)] md:h-[calc(100vh-130px)] pb-16 lg:pb-0 gap-2 md:gap-4" data-testid="admin-chats-container">
      {/* Conversations List - full width on mobile when no chat selected, side panel on desktop */}
      <div className={`${selectedCustomer ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-shrink-0 flex-col bg-white rounded-lg border overflow-hidden`}>
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 text-sm"
              data-testid="search-chats-input"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredConversations.length === 0 ? (
            <div className="p-6 text-center text-slate-400">
              <MessageCircle className="w-10 h-10 mx-auto mb-2" />
              <p className="text-sm">No conversations yet</p>
            </div>
          ) : (
            filteredConversations.map((conv) => (
              <button
                key={conv.customer_id}
                onClick={() => setSelectedCustomer(conv.customer_id)}
                className={`w-full p-3 text-left border-b hover:bg-slate-50 transition-colors ${
                  selectedCustomer === conv.customer_id ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                }`}
                data-testid={`chat-conv-${conv.customer_id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm text-slate-800 truncate">{conv.customer_name}</span>
                  {conv.unread_count > 0 && (
                    <Badge className="bg-red-500 text-white text-xs h-5 min-w-[20px] flex items-center justify-center">
                      {conv.unread_count}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 truncate">{conv.last_message}</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  {conv.last_time ? formatDistanceToNow(new Date(conv.last_time), { addSuffix: true }) : ''}
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-white rounded-lg border overflow-hidden">
        {!selectedCustomer ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
            <MessageCircle className="w-16 h-16 mb-4" />
            <p className="text-lg font-medium">Select a conversation</p>
            <p className="text-sm">Choose a customer from the list to start chatting</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setSelectedCustomer(null); prevMsgCount.current = 0; }}
                  className="md:hidden p-1"
                >
                  <ArrowLeft className="w-5 h-5 text-slate-600" />
                </button>
                <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{customerInfo?.full_name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {customerInfo?.phone}
                  </p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <p className="text-sm">No messages yet. Send the first message!</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isAdmin = msg.sender_type === 'admin';
                  return (
                    <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                        isAdmin
                          ? 'bg-indigo-600 text-white rounded-br-md'
                          : 'bg-white text-slate-800 rounded-bl-md shadow-sm border'
                      }`}>
                        {!isAdmin && (
                          <p className="text-xs font-medium text-indigo-600 mb-1">{msg.sender_name}</p>
                        )}
                        <p className="text-sm">{msg.message}</p>
                        <p className={`text-[10px] mt-1 ${isAdmin ? 'text-indigo-200' : 'text-slate-400'}`}>
                          {msg.created_at ? format(new Date(msg.created_at), 'hh:mm a') : ''}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={sendMessage} className="p-3 border-t flex items-center gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a reply..."
                className="flex-1"
                disabled={sending}
                data-testid="admin-chat-input"
              />
              <Button
                type="submit"
                size="icon"
                className="bg-indigo-600 hover:bg-indigo-700"
                disabled={sending || !newMessage.trim()}
                data-testid="admin-chat-send"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminChats;
